import { createSelector } from '@reduxjs/toolkit'
import { parseISO, differenceInDays, isValid } from 'date-fns'
import { useModal } from '@afterpaytouch/core'
import {
  BiometricState,
  CheckoutAppSelector,
  useAppSelector,
  useInitialData,
  useIsOverduePaymentsFlow,
  useIsTrustedCheckout,
  useOAuthStateSelector,
  useConsumerTag,
  useConsumerSelector,
  useIsPersistentAndOptedIn,
  isCredentialPresent,
  isBiometricCapableBrowser,
  isEligibleBiometricLogin,
  useIsPrivateBrowsingMode,
  useAppDispatch,
  checkIsBiometricEnabledOnServer,
  useCLRiskDeclineError,
  useCohort,
  useIsBillingAgreementApprovalProcess,
  useHasBillingAgreements,
} from '..'
import { shouldAttemptAccountLink } from '../../utils/oAuth'
import {
  BIOMETRIC_DENIED_COUNT_KEY,
  BIOMETRIC_LAST_DENIED_DATE_KEY,
  BIOMETRIC_DENIED_DAYS,
  BIOMETRIC_MAX_DENIED_COUNT,
  BiometricsCheckTrigger,
  PaymentOptionsErrorModalKey,
} from '../../model'
import { Cohort } from '@afterpaytouch/portal-api'

export const biometricSelector: CheckoutAppSelector<BiometricState> = (state) => state.biometric

export const hasSilentErrorsSelector: CheckoutAppSelector<boolean> = createSelector(biometricSelector, (state) => state?.hasSilentErrors)

export const useBiometric = (): BiometricState => useAppSelector(biometricSelector)

export const useHasSilentBiometricErrors = (): boolean => useAppSelector(hasSilentErrorsSelector)

export const useIsBiometricConflictingModal = (): boolean => {
  const showOverdueModal = useIsOverduePaymentsFlow()
  const { visible: showPaymentOptionsErrorModal } = useModal(PaymentOptionsErrorModalKey)
  const { isError: showCLRiskDeclineError } = useCLRiskDeclineError()

  return showOverdueModal || showPaymentOptionsErrorModal || showCLRiskDeclineError
}

export const useIsEligibleBiometricRegistration = (): boolean => {
  const { allowDisplayingModal } = useBiometric()
  const oAuthState = useOAuthStateSelector()
  const isBiometricCapableBrowser = useIsBiometricCapableBrowser()
  const isLoggedInWithOAuth = useIsTrustedCheckout()
  const isPersistentAndOptedIn = useIsPersistentAndOptedIn()
  const isPrivateBrowsingMode = useIsPrivateBrowsingMode()
  const hasBillingAgreements = useHasBillingAgreements()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()

  const { email, mobile, hasPassword, isUserVerifyAuthenticatorAvailable } = useConsumerSelector()
  const consumerTag = useConsumerTag()

  const isBiometricEnabled = isCredentialPresent({ email, mobile })

  const cohort = useCohort()

  if (cohort === Cohort.CashOnly || hasBillingAgreements || isBillingAgreementApprovalProcess) {
    return false
  }

  if (isUserVerifyAuthenticatorAvailable) {
    const hasOAuthParams = shouldAttemptAccountLink(oAuthState)

    const precondition =
      allowDisplayingModal &&
      isBiometricCapableBrowser &&
      !isBiometricEnabled &&
      !hasOAuthParams &&
      !isLoggedInWithOAuth &&
      hasPassword &&
      !isPersistentAndOptedIn &&
      !isPrivateBrowsingMode

    if (!precondition) {
      return false
    }

    if (!(BIOMETRIC_DENIED_COUNT_KEY in consumerTag) || !(BIOMETRIC_LAST_DENIED_DATE_KEY in consumerTag)) {
      return precondition
    }

    const deniedCountValue = consumerTag[BIOMETRIC_DENIED_COUNT_KEY]
    const lastDeniedDateValue = consumerTag[BIOMETRIC_LAST_DENIED_DATE_KEY]

    if (deniedCountValue == null || lastDeniedDateValue == null) {
      return precondition
    }

    const deniedCount = parseInt(deniedCountValue, 10)
    const lastDate = parseISO(lastDeniedDateValue)

    if (Number.isNaN(deniedCount) || !isValid(lastDate)) {
      return precondition
    }

    const diffDays = differenceInDays(new Date(), lastDate)

    return deniedCount < BIOMETRIC_MAX_DENIED_COUNT && diffDays >= BIOMETRIC_DENIED_DAYS
  }

  return false
}

export const useIsEligibleBiometricLogin = (): boolean => {
  const oAuthState = useOAuthStateSelector()
  const { isUserVerifyAuthenticatorAvailable } = useConsumerSelector()
  const isBiometricCapableBrowser = useIsBiometricCapableBrowser()
  const isLoggedInWithOAuth = useIsTrustedCheckout()
  const isPrivateBrowsingMode = useIsPrivateBrowsingMode()
  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly

  return isEligibleBiometricLogin({
    oAuthState,
    isBiometricCapableBrowser,
    isLoggedInWithOAuth,
    isUserVerifyAuthenticatorAvailable,
    isPrivateBrowsingMode,
    isCashOnlyCohort,
  })
}

export const useIsBiometricCapableBrowser = (): boolean => {
  const { isIframe } = useInitialData()

  return isBiometricCapableBrowser(isIframe)
}

export const useBiometricsEligibleAndRegisteredCheckTrigger = (): BiometricsCheckTrigger => {
  const dispatch = useAppDispatch()

  return async (isBiometricsEligiblePayload, isMobileLogin, email, mobile) => {
    const isBiometricEligible =
      typeof isBiometricsEligiblePayload === 'boolean' ? isBiometricsEligiblePayload : isEligibleBiometricLogin(isBiometricsEligiblePayload)

    return isBiometricEligible && (await dispatch(checkIsBiometricEnabledOnServer(email, mobile, isMobileLogin)))
  }
}

export const useShowBiometricsButton = (): boolean => {
  const { email, mobile } = useConsumerSelector()

  const { isBiometricEnabledOnServer = false } = useBiometric()
  const isBiometricEligible = useIsEligibleBiometricLogin()
  const hasSilentBiometricsErrors = useHasSilentBiometricErrors()

  return !hasSilentBiometricsErrors && !isCredentialPresent({ email, mobile }) && isBiometricEligible && isBiometricEnabledOnServer
}

export const useShouldAutoTriggerBiometrics = (): boolean => {
  const isBiometricEligible = useIsEligibleBiometricLogin()
  const { email, mobile } = useConsumerSelector()
  const { isBiometricEnabledOnServer = false } = useBiometric()

  return isCredentialPresent({ email, mobile }) && isBiometricEligible && isBiometricEnabledOnServer
}
